import arrowRightAltIcon from '@iconify/icons-material-symbols/arrow-right-alt';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useUserHasRole } from '#pie/auth/useUserHasRole';
import { Card } from '#pie/components/card/Card';
import { DescriptionList } from '#pie/components/description-list/DescriptionList';
import { DescriptionListItem } from '#pie/components/description-list/description-list-item/DescriptionListItem';
import { Divider } from '#pie/components/divider/Divider';
import { Icon } from '#pie/components/icon/Icon';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { getFormattedDate } from '#pie/i18n/dateFormats';
import {
  getProductKindIcon,
  getProvidedServiceIcon,
} from '#pie/utils/textToIcon';
import type { MarketPartyRegistration } from './marketPartyRegistrationAdapter';

interface MarketPartyRegistrationDetailsProps {
  marketParty: MarketPartyRegistration;
  showQuickLinks?: boolean;
}

export const MarketPartyRegistrationDetails = ({
  marketParty,
  showQuickLinks = false,
}: MarketPartyRegistrationDetailsProps) => {
  const { t } = useTranslation();
  const { t: tmp } = useTranslation('marketParty');
  const showMarketActivitiesProduct = useUserHasRole({
    marketRole: ['LV', 'MV', 'PV', 'GDS', 'RNB', 'LNB'],
  });
  const showMarketActivitiesService = useUserHasRole({
    marketRole: ['CSP', 'BSP'],
  });

  const seenServiceCategories = new Set<string>();
  const uniqueServiceCategories = marketParty.serviceCategories?.filter(
    ({ marketSegment, product, providedService }) => {
      if (showMarketActivitiesProduct && (!product || !marketSegment)) {
        return false;
      }

      const key = showMarketActivitiesProduct
        ? `${product}-${marketSegment}`
        : providedService;

      if (!key || seenServiceCategories.has(key)) {
        return false;
      }

      seenServiceCategories.add(key);
      return true;
    },
  );

  return (
    <Card
      size="md"
      heading={
        <Text as="h2" variant="h6" className="text-content-heading">
          {tmp('title')}
        </Text>
      }
    >
      <Stack gap="md" className="mx-auto mb-4 max-w-4xl">
        <DescriptionList variant="grid">
          <DescriptionListItem
            direction="vertical"
            title={tmp('registration_detail.statutory_name')}
          >
            {`${marketParty.statutoryName} (${tmp(
              `marketParty.marketRole.${marketParty.marketRole}_abbreviations`,
              {
                defaultValue: '',
              },
            )})`}
          </DescriptionListItem>
          <DescriptionListItem direction="vertical" title={tmp('market_role')}>
            {`${tmp(
              `marketParty.marketRole.${marketParty.marketRole}_abbreviations`,
              {
                defaultValue: '',
              },
            )} (${tmp(`marketParty.marketRole.${marketParty.marketRole}`, {
              defaultValue: '',
            })})`}
          </DescriptionListItem>
          <DescriptionListItem
            direction="vertical"
            title={tmp('registration_detail.participant_eancode')}
          >
            {marketParty.marketParticipantEanCode}
          </DescriptionListItem>
          <DescriptionListItem direction="vertical" title={t('common.status')}>
            {tmp(`marketParty.status.${marketParty.status}`, {
              defaultValue: '-',
            })}
          </DescriptionListItem>
          <DescriptionListItem
            direction="vertical"
            title={tmp('registration_detail.startdate')}
          >
            {marketParty.marketParticipantStartDate &&
              getFormattedDate(
                new Date(marketParty.marketParticipantStartDate),
                false,
              )}
          </DescriptionListItem>
        </DescriptionList>
      </Stack>

      {(showMarketActivitiesProduct || showMarketActivitiesService) &&
        (uniqueServiceCategories?.length ?? 0) > 0 && (
          <Stack gap="md" className="mx-auto mb-2 max-w-4xl">
            <Divider />
            <Text as="h2" variant="h6" className="text-content-heading">
              {tmp('registration_detail.market_activities')}
            </Text>
          </Stack>
        )}

      {uniqueServiceCategories?.map(
        (serviceCategory) =>
          (showMarketActivitiesProduct && (
            <Stack gap="sm" className="mx-auto max-w-4xl">
              <section className="bg-white mt-2 rounded-md border border-border-divider p-4">
                <Stack direction="row" gap="xs" align="center">
                  {getProductKindIcon(serviceCategory.product) && (
                    <Icon
                      icon={getProductKindIcon(serviceCategory.product)!}
                      size={24}
                      className="text-content-heading"
                    />
                  )}
                  <Text className="text-content-heading font-bold">
                    {t(`common.product${serviceCategory?.product}`, {
                      defaultValue: '',
                    })}{' '}
                    {tmp(
                      `serviceCategory.marketSegment.${serviceCategory?.marketSegment}`,
                      {
                        defaultValue: '',
                      },
                    )}
                  </Text>
                </Stack>
              </section>
            </Stack>
          )) ||
          (showMarketActivitiesService && (
            <Stack gap="sm" className="mx-auto max-w-4xl">
              <section className="bg-white mt-2 rounded-md border border-border-divider p-4">
                <Stack direction="row" gap="xs" align="center">
                  {getProvidedServiceIcon(serviceCategory.providedService) && (
                    <Icon
                      icon={
                        getProvidedServiceIcon(serviceCategory.providedService)!
                      }
                      size={24}
                      className="text-content-heading"
                    />
                  )}
                  <Text className="text-content-heading font-bold">
                    {tmp(
                      `marketParty.service.${serviceCategory?.providedService}`,
                      {
                        defaultValue: '',
                      },
                    )}
                  </Text>
                </Stack>
              </section>
            </Stack>
          )),
      )}

      {showQuickLinks && (
        <Stack gap="sm" className="mx-auto mt-2 max-w-4xl">
          <div className="flex h-12 items-center gap-2 border-b border-border-minimal-enabled">
            <Text as="h3" className="text-content-heading font-bold">
              {tmp('registration_detail.quick_links')}
            </Text>
          </div>
          <Link
            className="flex w-fit items-center gap-1 text-action-primary-enabled underline"
            to={`/contactpersonenregister/contact/?filters=${btoa(
              JSON.stringify({ marketPartyIds: [marketParty.dynamicsId] }),
            )}`}
          >
            {t('cpr_overview.page_title')}
            <Icon icon={arrowRightAltIcon} size={24} aria-hidden />
          </Link>
        </Stack>
      )}
    </Card>
  );
};
